import { useSearchParams } from "react-router-dom";

type Updater<T> = Partial<T> | ((prev: T) => Partial<T>);

export function useUrlState<T extends Record<string, string | number>>(
  defaults: T,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = Object.fromEntries(
    Object.entries(defaults).map(([key, def]) => {
      const raw = searchParams.get(key);
      if (raw == null) return [key, def];
      if (typeof def === "number") {
        const num = Number(raw);
        return [key, Number.isFinite(num) && num >= 0 ? num : def];
      }
      return [key, raw];
    }),
  ) as T;

  const setState = (updater: Updater<T>) => {
    const partial = typeof updater === "function" ? updater(state) : updater;
    // Skip no-op updates entirely. Without this guard, a setState call
    // with unchanged filter values would still trigger the page-reset
    // logic below, clobbering a deep-linked page (e.g. /jobs?search=foo&page=3
    // would reset to page 1 when the debounce fires on mount with the same
    // search value).
    const hasChange = Object.entries(partial).some(([k, v]) => state[k] !== v);
    if (!hasChange) return;

    const next = new URLSearchParams(searchParams);
    const hasNonPageChange = Object.keys(partial).some((k) => k !== "page");
    for (const [k, v] of Object.entries(partial)) {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    }
    if (hasNonPageChange && !("page" in partial)) next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  return { state, setState };
}
